import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRef, useEffect } from "react";
import { LeaderboardPlayerData } from "../../models/leaderboard";
import { Group } from "../../models/groupedTeams";
import { MatchSetupConfig } from "../../models/match";
import { MobileGroupCard } from "./leaderboardDataRows/MobileGroupCard";
import { MobileTeamCard } from "./leaderboardDataRows/MobileTeamCard";
import { MobilePlayerCard } from "./leaderboardDataRows/MobilePlayerCard";
import { PlayerDataRow } from "./leaderboardDataRows/PlayerDataRow";
import { TeamDataRow } from "./leaderboardDataRows/TeamDataRow";
import { GroupDataRow } from "./leaderboardDataRows/GroupDataRow";

interface LeaderboardTableProps {
  data: LeaderboardPlayerData[];
  view: "group" | "team" | "player";
  groups: Group[];
  matchSetup?: MatchSetupConfig;
}

const getTeamData = (
  groups: any[] | undefined,
  data: LeaderboardPlayerData[] | undefined,
  matchSetup?: MatchSetupConfig
) => {
  const teamMap = new Map<
    string,
    {
      teamId: string;
      teamName: string;
      teamLogo: string;
      groupName: string;
      players: LeaderboardPlayerData[];
    }
  >();

  data?.forEach((player: LeaderboardPlayerData) => {
    const teamId = player.playerForTeamId;
    const teamName = player.playerForTeamName;
    const teamLogo = player.playerForTeamLogo;

    if (!teamId || !teamName) return;

    if (!teamMap.has(teamId)) {
      // Find the group that contains this team
      const group = groups?.find((g) => g.teamIds?.includes(teamId));

      teamMap.set(teamId, {
        teamId,
        teamName,
        teamLogo: teamLogo || "",
        groupName: group?.name || "Ungrouped",
        players: [],
      });
    }

    const team = teamMap.get(teamId)!;
    team.players.push(player);
  });
  if (matchSetup && matchSetup.gameFormat === "matchplay") {
    return Array.from(teamMap.values())
      .map((team: any) => {
        return {
          ...team,
          totalScore: team.players
            .sort((a: any, b: any) => b.totalPoints - a.totalPoints)
            .reduce((sum: number, player: any) => sum + player.totalPoints, 0),
        };
      })
      .sort((a, b) => b.totalScore - a.totalScore);
  } else if (matchSetup && matchSetup.playFormat === "doubles") {
    return Array.from(teamMap.values())
      .map((team: any) => {
        const selections = matchSetup?.playerSelections?.find(
          (s) => s.teamId === team.teamId
        );

        if (!selections) return team;
        const playerPairs = selections?.players.reduce((acc, player) => {
          const holeDescription = player.holeDescription || "Unknown Hole";
          if (!acc[holeDescription]) {
            acc[holeDescription] = [];
          }
          acc[holeDescription].push(player);
          return acc;
        }, {} as Record<string, any[]>);

        const selectedPlayerIds: string[] = Object.values(playerPairs).map(
          (players) => players[0].playerId
        );
        const teamPlayers = team.players.filter((player: any) =>
          selectedPlayerIds.includes(player.playerId)
        );

        return {
          ...team,
          players: teamPlayers,
          totalScore: teamPlayers
            .sort((a: any, b: any) => b.totalStableford - a.totalStableford)
            .slice(0, 3)
            .reduce(
              (sum: number, player: any) => sum + player.totalStableford,
              0
            ),
        };
      })
      .sort((a, b) => b.totalScore - a.totalScore);
  } else {
    // Transform and sort teams based on score first
    return Array.from(teamMap.values())
      .map((team) => ({
        ...team,
        totalScore: team.players
          .sort((a, b) => b.totalStableford - a.totalStableford)
          .slice(0, 6)
          .reduce((sum, player) => sum + player.totalStableford, 0),
      }))
      .sort((a, b) => {
        // Sort by score first
        return b.totalScore - a.totalScore;
      });
  }
};
export function LeaderboardTable({
  data,
  view,
  groups,
  matchSetup,
}: LeaderboardTableProps) {
  const navigate = useNavigate();
  const itemsRef = useRef<{ [key: string]: HTMLElement }>({});
  const [teamData, setTeamData] = useState<any[] | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (data && groups) {
      setTeamData(getTeamData(groups, data, matchSetup));
    }
  }, [view, data, groups]);

  const getAllScores = () => {
    if (view === "team" && teamData) {
      return teamData.map((team) => team.totalScore);
    } else if (view === "player" && data) {
      return data.map((player) => player.totalStableford ?? player.matchResult);
    }
    return [];
  };

  return (
    <>
      {/* Mobile Card View */}
      <div className="md:hidden space-y-4">
        {view === "group"
          ? groups.map((group, index) => (
              <MobileGroupCard
                group={group}
                index={index}
                expandedGroups={expandedGroups}
                setExpandedGroups={setExpandedGroups}
                teamData={teamData || []}
                navigate={navigate}
              />
            ))
          : view === "team"
          ? teamData?.map((team, index) => (
              <MobileTeamCard
                team={team}
                itemsRef={itemsRef}
                index={index}
                navigate={navigate}
              />
            ))
          : data
              .sort((a, b) => b.totalStableford - a.totalStableford)
              .map((player, index) => (
                <MobilePlayerCard
                  key={player.id}
                  index={index}
                  player={player}
                  itemsRef={itemsRef}
                  navigate={navigate}
                  matchSetup={matchSetup}
                />
              ))}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden transition-colors duration-200">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-12">
                Sno.
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {view === "group"
                  ? "Group"
                  : view === "team"
                  ? "Team"
                  : "Player"}
              </th>
              {view === "player" && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Team
                </th>
              )}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {view === "group" ? "Teams" : "Score"}
              </th>
              {matchSetup?.gameFormat !== "matchplay" && (
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-12">
                  Details
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {view === "group"
              ? groups.map((group, index) => (
                  <GroupDataRow
                    key={group.id}
                    group={group}
                    index={index}
                    navigate={navigate}
                    teamData={teamData || []}
                    expandedGroups={expandedGroups}
                    setExpandedGroups={setExpandedGroups}
                  />
                ))
              : view === "team"
              ? teamData?.map((team, index) => (
                  <TeamDataRow
                    key={team.teamId}
                    team={team}
                    index={index}
                    navigate={navigate}
                    allScores={getAllScores()}
                  />
                ))
              : data
                  .sort((a, b) => b.totalStableford - a.totalStableford)
                  .map((player, index) => (
                    <PlayerDataRow
                      key={player.id}
                      player={player}
                      index={index}
                      navigate={navigate}
                      matchSetup={matchSetup}
                    />
                  ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
